/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  COMMUNICATION_METHODS,
  CRY_AFTER_BIRTH,
  DELIVERY_TYPES,
  DRESSING_SKILLS,
  FINE_MOTOR_SKILLS,
  FOOD_TEXTURES,
  GROSS_MOTOR_SKILLS,
  INTAKE_GENDERS,
  INTAKE_TRISTATE,
  MEAL_COMPLETION_TIMES,
  PREGNANCY_DURATIONS,
  SAFETY_AWARENESS,
  SELFCARE_LEVELS,
  SPECIAL_DIETS,
  TOILETING_SKILLS,
} from '@therapy/shared-validators';

import type { IntakeSection, IntakeSubmitInput } from '@therapy/shared-validators';
import type { FieldPath } from 'react-hook-form';

/** How a field renders (maps to a primitive in intake-section.tsx). */
export type FieldKind =
  | 'text'
  | 'number'
  | 'date'
  | 'textarea'
  | 'select'
  | 'choice'
  | 'multichoice'
  | 'grid'
  | 'guardians';

/** A dependent field is shown only when `name`'s value is one of `in`. */
export interface ShowWhen {
  readonly name: FieldPath<IntakeSubmitInput>;
  readonly in: readonly string[];
}

export interface FieldDef {
  readonly name: FieldPath<IntakeSubmitInput>;
  /** i18n leaf — label is `intake.field.<labelKey>`, hint `intake.hint.<labelKey>`. */
  readonly labelKey: string;
  readonly kind: FieldKind;
  readonly options?: readonly string[];
  /** i18n prefix for option labels: `intake.opt.<optPrefix>.<value>`. */
  readonly optPrefix?: string;
  readonly hasHint?: boolean;
  /** When set, the field renders only if the controlling field matches. */
  readonly showWhen?: ShowWhen;
}

const f = (name: FieldPath<IntakeSubmitInput>, labelKey: string, kind: FieldKind): FieldDef => ({
  name,
  labelKey,
  kind,
});
/** Wrap a field so it only shows when `controls` has one of `values`. */
const dependent = (
  def: FieldDef,
  controls: FieldPath<IntakeSubmitInput>,
  ...values: string[]
): FieldDef => ({ ...def, showWhen: { name: controls, in: values } });
const choice = (
  name: FieldPath<IntakeSubmitInput>,
  labelKey: string,
  options: readonly string[],
  optPrefix: string,
): FieldDef => ({ name, labelKey, kind: 'choice', options, optPrefix });
const yesNo = (name: FieldPath<IntakeSubmitInput>, labelKey: string): FieldDef =>
  choice(name, labelKey, INTAKE_TRISTATE, 'tristate');

/** The eight intake sections as ordered field descriptors (1:1 with the BRD form). */
export const INTAKE_SECTION_DEFS: Record<IntakeSection, readonly FieldDef[]> = {
  basicDetails: [
    f('basicDetails.firstName', 'firstName', 'text'),
    f('basicDetails.lastName', 'lastName', 'text'),
    f('basicDetails.dateOfBirth', 'dateOfBirth', 'date'),
    choice('basicDetails.gender', 'gender', INTAKE_GENDERS, 'gender'),
    f('basicDetails.primaryLanguage', 'primaryLanguage', 'text'),
    f('basicDetails.addressLine1', 'addressLine1', 'text'),
    f('basicDetails.addressLine2', 'addressLine2', 'text'),
    f('basicDetails.city', 'city', 'text'),
    f('basicDetails.state', 'state', 'text'),
    f('basicDetails.pinCode', 'pinCode', 'text'),
    f('basicDetails.primaryContact', 'primaryContact', 'text'),
    f('basicDetails.secondaryContact', 'secondaryContact', 'text'),
    f('basicDetails.email', 'email', 'text'),
    f('basicDetails.guardians', 'guardians', 'guardians'),
  ],
  birthHistory: [
    choice('birthHistory.pregnancyDuration', 'pregnancyDuration', PREGNANCY_DURATIONS, 'pregnancy'),
    choice('birthHistory.deliveryType', 'deliveryType', DELIVERY_TYPES, 'delivery'),
    f('birthHistory.birthWeightKg', 'birthWeightKg', 'number'),
    choice('birthHistory.criedAfterBirth', 'criedAfterBirth', CRY_AFTER_BIRTH, 'cry'),
    yesNo('birthHistory.jaundiceHistory', 'jaundiceHistory'),
    dependent(
      f('birthHistory.jaundiceDetails', 'jaundiceDetails', 'text'),
      'birthHistory.jaundiceHistory',
      'yes',
      'sometimes',
    ),
    yesNo('birthHistory.appearanceChanges', 'appearanceChanges'),
    dependent(
      f('birthHistory.appearanceDetails', 'appearanceDetails', 'text'),
      'birthHistory.appearanceChanges',
      'yes',
      'sometimes',
    ),
  ],
  postNatal: [
    yesNo('postNatal.breastfeeding', 'breastfeeding'),
    yesNo('postNatal.seizureHistory', 'seizureHistory'),
    dependent(
      f('postNatal.seizureDetails', 'seizureDetails', 'text'),
      'postNatal.seizureHistory',
      'yes',
      'sometimes',
    ),
    yesNo('postNatal.complicationsAfterBirth', 'complicationsAfterBirth'),
    dependent(
      f('postNatal.complicationDetails', 'complicationDetails', 'text'),
      'postNatal.complicationsAfterBirth',
      'yes',
      'sometimes',
    ),
  ],
  milestones: [
    f('milestones.neckControlAge', 'neckControlAge', 'number'),
    yesNo('milestones.didRoll', 'didRoll'),
    yesNo('milestones.didCrawl', 'didCrawl'),
    f('milestones.ageSat', 'ageSat', 'number'),
    f('milestones.ageStood', 'ageStood', 'number'),
    f('milestones.ageStartedRunning', 'ageStartedRunning', 'number'),
  ],
  motorSkills: [
    {
      name: 'motorSkills.gross',
      labelKey: 'gross',
      kind: 'grid',
      options: GROSS_MOTOR_SKILLS,
      optPrefix: 'gross',
    },
    {
      name: 'motorSkills.fine',
      labelKey: 'fine',
      kind: 'grid',
      options: FINE_MOTOR_SKILLS,
      optPrefix: 'fine',
    },
    f('motorSkills.allergies', 'allergies', 'text'),
    choice('motorSkills.safety.strangers', 'safetyStrangers', SAFETY_AWARENESS, 'safety'),
    choice('motorSkills.safety.traffic', 'safetyTraffic', SAFETY_AWARENESS, 'safety'),
    choice('motorSkills.safety.hazards', 'safetyHazards', SAFETY_AWARENESS, 'safety'),
    choice('motorSkills.safety.wandering', 'safetyWandering', SAFETY_AWARENESS, 'safety'),
    choice('motorSkills.safety.mouthing', 'safetyMouthing', SAFETY_AWARENESS, 'safety'),
    choice('motorSkills.specialDiet', 'specialDiet', SPECIAL_DIETS, 'diet'),
    dependent(
      f('motorSkills.specialDietOther', 'specialDietOther', 'text'),
      'motorSkills.specialDiet',
      'other',
    ),
  ],
  dailyLiving: [
    yesNo('dailyLiving.feedsIndependently', 'feedsIndependently'),
    yesNo('dailyLiving.screenTimeDuringMeals', 'screenTimeDuringMeals'),
    choice(
      'dailyLiving.mealCompletionTime',
      'mealCompletionTime',
      MEAL_COMPLETION_TIMES,
      'mealTime',
    ),
    {
      name: 'dailyLiving.foodTextures',
      labelKey: 'foodTextures',
      kind: 'multichoice',
      options: FOOD_TEXTURES,
      optPrefix: 'texture',
    },
    yesNo('dailyLiving.drinksWaterAfterEachServing', 'drinksWaterAfterEachServing'),
    f('dailyLiving.sleepingPatterns', 'sleepingPatterns', 'textarea'),
    choice('dailyLiving.toiletingSkills', 'toiletingSkills', TOILETING_SKILLS, 'toileting'),
    choice('dailyLiving.brushingSkills', 'brushingSkills', SELFCARE_LEVELS, 'selfcare'),
    choice('dailyLiving.bathingSkills', 'bathingSkills', SELFCARE_LEVELS, 'selfcare'),
    choice('dailyLiving.dressingSkills', 'dressingSkills', DRESSING_SKILLS, 'dressing'),
  ],
  speechLanguage: [
    {
      name: 'speechLanguage.communicationMethods',
      labelKey: 'communicationMethods',
      kind: 'multichoice',
      options: COMMUNICATION_METHODS,
      optPrefix: 'comm',
    },
    f('speechLanguage.meaningfulWordCount', 'meaningfulWordCount', 'number'),
    yesNo('speechLanguage.doesBabble', 'doesBabble'),
    yesNo('speechLanguage.canAskForThings', 'canAskForThings'),
    dependent(
      f('speechLanguage.asksForItems', 'asksForItems', 'text'),
      'speechLanguage.canAskForThings',
      'yes',
      'sometimes',
    ),
    f('speechLanguage.canLabelObjects', 'canLabelObjects', 'textarea'),
    yesNo('speechLanguage.imitatesSingleWords', 'imitatesSingleWords'),
    yesNo('speechLanguage.imitatesPhrases', 'imitatesPhrases'),
    yesNo('speechLanguage.repeatsMemorizedSpeech', 'repeatsMemorizedSpeech'),
    dependent(
      f('speechLanguage.memorizedSpeechDetails', 'memorizedSpeechDetails', 'text'),
      'speechLanguage.repeatsMemorizedSpeech',
      'yes',
      'sometimes',
    ),
    yesNo('speechLanguage.fillsBlanksInSongs', 'fillsBlanksInSongs'),
    dependent(
      f('speechLanguage.songsList', 'songsList', 'text'),
      'speechLanguage.fillsBlanksInSongs',
      'yes',
    ),
  ],
  preferencesGoals: [
    f('preferencesGoals.favoritesGamesRhymesFood', 'favoritesGamesRhymesFood', 'textarea'),
    f('preferencesGoals.expectedChangesPostTherapy', 'expectedChangesPostTherapy', 'textarea'),
  ],
};
